import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import PhoneIcon from "@mui/icons-material/Phone";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import WebIcon from "@mui/icons-material/Web";
import CopyIcon from "@mui/icons-material/ContentCopy";
import LocationIcon from "@mui/icons-material/LocationOn";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { doc, getDoc } from "firebase/firestore/lite";
import db from "../../firebase";
import Grid from "@mui/material/Unstable_Grid2";

import {
  Typography,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Chip,
  Stack,
  Backdrop,
  Card,
  CardActionArea,
} from "@mui/material";

export const ActivitatPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [activity, setActivity] = useState({} as activitatDB);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("" as string);

  interface activitatDB {
    id?: string;
    title?: string;
    desc?: string;
    price?: string;
    image?: string;
    docs?: Array<string>;
    contact?: {
      whats?: number;
      tel?: number;
      email?: string;
      link?: string;
    };
    address?: string;
    category?: string;
    subcategory?: string;
    top?: boolean;
    topOrder?: number;
    categoryOrder?: number;
  }

  useEffect(() => {
    loadActivity();
  }, [id, location]);

  const loadActivity = async () => {
    setLoading(true);
    setError("");

    try {
      if (location.state?.activity) {
        setActivity(location.state.activity);
        setLoading(false);
        return;
      }

      const docRef = doc(db, "activitats/" + id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        setActivity({
          id: docSnap.id,
          ...docSnap.data(),
        });
      } else {
        console.error("Activity not found");
        setError("Activitat no trobada");
      }
    } catch (error) {
      console.error("Error loading activity:", error);
      setError("Error carregant l'activitat");
    } finally {
      setLoading(false);
    }
  };

  const handleCall = (tel: number) => {
    window.location.href = `tel:+376${tel}`;
  };

  const handleWhatsApp = (whats: number) => {
    window.open(`https://wa.me/${whats}`, "_blank");
  };

  const handleWebsite = (link: string) => {
    window.open(link, "_blank");
  };

  const handleCopyAddress = () => {
    navigator.clipboard.writeText(activity.address!);
  };

  const openMaps = () => {
    const encodedAddress = encodeURIComponent(activity.address!);
    window.open(
      `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`,
      "_blank"
    );
  };

  const handleBack = () => {
    if (location.key !== "default") {
      navigate(-1);
    } else {
      navigate("/activitats");
    }
  };

  if (loading) {
    return (
      <Backdrop open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  if (error || !activity) {
    return (
      <Grid container spacing={2}>
        <Grid xs={12} mdOffset={1} md={10} sx={{ px: 2, pt: 2 }}>
          <Box textAlign="center" py={6}>
            <Typography variant="h5" color="error" gutterBottom>
              {error || "Activitat no trobada"}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    );
  }
  return (
    <Box>
      {/* Back button sticky (sempre visible) */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1100,
          py: 1.5,
          px: 2,
        }}
      >
        <Grid container spacing={0}>
          <Grid xs={12} mdOffset={1} md={10}>
            <IconButton
              onClick={handleBack}
              sx={{
                bgcolor: "rgba(255,255,255,0.9)",
                "&:hover": { bgcolor: "rgba(255,255,255,1)" },
                boxShadow: 1,
              }}
            >
              <ArrowBackIcon />
            </IconButton>
          </Grid>
        </Grid>
      </Box>

      <Grid container spacing={2} sx={{ mt: -8 }}>
        {" "}
        {/* ← Margin negatiu per sobreposar */}
        {/* Imatge principal */}
        <Grid xs={12} mdOffset={1} md={10} sx={{ px: 2 }}>
          <Box
            component="img"
            src={activity.image || "https://via.placeholder.com/1200x400"}
            alt={activity.title}
            sx={{
              width: "100%",
              height: { xs: 250, sm: 350, md: 400 },
              objectFit: "cover",
              borderRadius: 1,
            }}
          />
        </Grid>
        {/* Contingut */}
        <Grid xs={12} mdOffset={1} md={10} sx={{ px: 2 }}>
          {/* Categories i subcategoria */}
          <Stack direction="row" spacing={1} mb={1} flexWrap="wrap" useFlexGap>
            {activity.category && (
              <Chip label={activity.category} variant="outlined" size="small" />
            )}
            {activity.subcategory && (
              <Chip
                label={activity.subcategory}
                variant="outlined"
                size="small"
              />
            )}
          </Stack>

          {/* Títol */}
          <Typography variant="h4" gutterBottom fontWeight="bold">
            {activity.title}
          </Typography>

          {/* Preu */}
          <Typography variant="h6" gutterBottom fontWeight="600" mb={3}>
            {activity.price}
          </Typography>

          {/* Descripció */}
          <Box mb={3}>
            <Typography variant="h6" gutterBottom fontWeight="600">
              Descripció
            </Typography>
            <Typography
              variant="body1"
              sx={{ whiteSpace: "pre-line", lineHeight: 1.8 }}
            >
              {activity.desc}
            </Typography>
          </Box>

          {/* Ubicació */}
          <Box mb={3}>
            <Typography variant="h6" gutterBottom fontWeight="600">
              Ubicació
            </Typography>
            <Card
              sx={{
                border: 1,
                borderColor: "grey.300",
                cursor: "pointer",
                "&:hover": { bgcolor: "grey.50" },
              }}
              onClick={openMaps}
            >
              <Box sx={{ px: 2, py: 2 }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={2}
                >
                  <Typography>{activity.address}</Typography>
                  <LocationIcon />
                </Stack>
              </Box>
            </Card>
          </Box>

          {/* Contacte */}
          <Box mb={4}>
            <Typography variant="h6" gutterBottom fontWeight="600">
              Contacte
            </Typography>
            <Stack spacing={2}>
              {activity.contact?.tel && (
                <Card
                  sx={{
                    border: 1,
                    borderColor: "grey.300",
                    cursor: "pointer",
                    "&:hover": { bgcolor: "grey.50" },
                  }}
                  onClick={() =>
                    activity.contact?.tel && handleCall(activity.contact.tel)
                  }
                >
                  <Box sx={{ px: 2, py: 2 }}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      spacing={2}
                    >
                      <Typography>
                        Trucar: +376 {activity.contact.tel}
                      </Typography>
                      <PhoneIcon />
                    </Stack>
                  </Box>
                </Card>
              )}

              {activity.contact?.whats && (
                <Card
                  sx={{
                    border: 1,
                    borderColor: "grey.300",
                    cursor: "pointer",
                    "&:hover": { bgcolor: "grey.50" },
                  }}
                  onClick={() =>
                    activity.contact?.whats &&
                    handleWhatsApp(activity.contact.whats)
                  }
                >
                  <Box sx={{ px: 2, py: 2 }}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      spacing={2}
                    >
                      <Typography>WhatsApp</Typography>
                      <WhatsAppIcon />
                    </Stack>
                  </Box>
                </Card>
              )}

              {activity.contact?.link && (
                <Card
                  sx={{
                    border: 1,
                    borderColor: "grey.300",
                    cursor: "pointer",
                    "&:hover": { bgcolor: "grey.50" },
                  }}
                  onClick={() =>
                    activity.contact?.link &&
                    handleWebsite(activity.contact.link)
                  }
                >
                  <Box sx={{ px: 2, py: 2 }}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      spacing={2}
                    >
                      <Typography>Lloc web</Typography>
                      <WebIcon />
                    </Stack>
                  </Box>
                </Card>
              )}
            </Stack>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};
